import { fetchWithAuth } from "../utils/auth";
import { API_URL } from "../utils/constants";
import { ILease } from "./leaseService";
import { IPropertyManagementContract } from "./pmContractService";
import { IUnit, IUnitOwner } from "./unitService";
import { TFunction } from "i18next";

export enum DocumentType {
  PASSPORT = "passport",
  PROOF_OF_ADDRESS = "proof-of-address",
  EMIRATES_ID = "emirates-id",
  VISA = "visa",
  TENANCY_CONTRACT = "tenancy-contract",
  EJARI = "ejari",
  PURCHASE_CONTRACT = "purchase-contract",
  TITLE_DEED = "title-deed",
  OQOOD = "oqood",
  POWER_OF_ATTORNEY = "power-of-attorney",
  PROPERTY_MANAGEMENT_CONTRACT = "property-management-contract",
  SNAGGING_REPORT = "snagging-report",
  DE_SNAGGING_REPORT = "de-snagging-report",
  INVOICE = "invoice",
  RECEIPT = "receipt",
  MOVE_IN_REPORT = "move-in-report",
  MOVE_OUT_REPORT = "move-out-report",
  INSPECTION_REPORT = "inspection-report",
  PRE_INSPECTION_REPORT = "pre-inspection-report",
  SALARY_CERTIFICATE = "salary-certificate",
  RESIDUAL_VALUE_APPRAISAL = "residual-value-appraisal",
  RESIDUAL_VALUE_APPRAISAL_COVER_LETTER = "residual-value-appraisal-cover-letter",
  CREDIT_REPORT = "credit-report",
  COMPLETION_CERTIFICATE = "completion-certificate",
  KEY_HANDOVER_PROTOCOL = "key-handover-protocol",
  DEWA_REGISTRATION = "dewa-registration",
  EMPOWER_REGISTRATION = "empower-registration",
  LEGAL_NOTICE = "legal-notice",
}

export const formatDocumentTypeWithTranslation =
  (t: TFunction) => (documentType: DocumentType | null) => {
    switch (documentType) {
      case DocumentType.PASSPORT:
        return t("document_types.passport");
      case DocumentType.PROOF_OF_ADDRESS:
        return t("document_types.proof_of_address");
      case DocumentType.EMIRATES_ID:
        return t("document_types.emirates_id");
      case DocumentType.VISA:
        return t("document_types.visa");
      case DocumentType.TENANCY_CONTRACT:
        return t("document_types.tenancy_contract");
      case DocumentType.EJARI:
        return t("document_types.ejari");
      case DocumentType.SNAGGING_REPORT:
        return t("document_types.snagging_report");
      case DocumentType.DE_SNAGGING_REPORT:
        return t("document_types.de_snagging_report");
      case DocumentType.PURCHASE_CONTRACT:
        return t("document_types.purchase_contract");
      case DocumentType.TITLE_DEED:
        return t("document_types.title_deed");
      case DocumentType.OQOOD:
        return t("document_types.oqood");
      case DocumentType.POWER_OF_ATTORNEY:
        return t("document_types.power_of_attorney");
      case DocumentType.PROPERTY_MANAGEMENT_CONTRACT:
        return t("document_types.property_management_contract");
      case DocumentType.INVOICE:
        return t("document_types.invoice");
      case DocumentType.RECEIPT:
        return t("document_types.receipt");
      case DocumentType.SALARY_CERTIFICATE:
        return t("document_types.salary_certificate");
      case DocumentType.MOVE_IN_REPORT:
        return t("document_types.move_in_report");
      case DocumentType.MOVE_OUT_REPORT:
        return t("document_types.move_out_report");
      case DocumentType.INSPECTION_REPORT:
        return t("document_types.inspection_report");
      case DocumentType.PRE_INSPECTION_REPORT:
        return t("document_types.pre_inspection_report");
      case DocumentType.RESIDUAL_VALUE_APPRAISAL:
        return t("document_types.residual_value_appraisal");
      case DocumentType.RESIDUAL_VALUE_APPRAISAL_COVER_LETTER:
        return t("document_types.residual_value_appraisal_cover_letter");
      case DocumentType.CREDIT_REPORT:
        return "Credit Report";
      case DocumentType.KEY_HANDOVER_PROTOCOL:
        return t("document_types.key_handover_protocol");
      case DocumentType.COMPLETION_CERTIFICATE:
        return t("document_types.completion_certificate");
      case DocumentType.DEWA_REGISTRATION:
        return t("document_types.dewa_registration");
      case DocumentType.EMPOWER_REGISTRATION:
        return t("document_types.empower_registration");
      case DocumentType.LEGAL_NOTICE:
        return t("document_types.legal_notice");
      default:
        return t("document_types.unknown");
    }
  };

export enum DocumentStatus {
  ACCEPTED = "accepted",
  IN_REVIEW = "in_review",
  DECLINED = "declined",
}

export const formatDocumentStatus = (status: DocumentStatus, t: TFunction) => {
  switch (status) {
    case DocumentStatus.ACCEPTED:
      return t("service_status.document.accepted");
    case DocumentStatus.IN_REVIEW:
      return t("service_status.document.in_review");
    case DocumentStatus.DECLINED:
      return t("service_status.document.declined");
    default:
      return t("service_status.document.unknown");
  }
};

export const formatDocumentStatusWithTranslation =
  (t: TFunction) => (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.ACCEPTED:
        return t("document_status.accepted");
      case DocumentStatus.IN_REVIEW:
        return t("document_status.in_review");
      case DocumentStatus.DECLINED:
        return t("document_status.declined");
      default:
        return t("document_status.unknown");
    }
  };

export interface IDocument {
  document_id: number;
  document_type?: DocumentType | null;
  status: DocumentStatus;
  reason_for_refusal: string | null;
  file_type: string;
  file_path: string;
  file_name: string;
  valid_until: string | null;
  internal_document: boolean;
  document_identifier: string | null;
  created_at: string;
  updated_at: string;
  property_id?: number | null;
  unit?: IUnit | null;
  lease?: ILease | null;
  unit_owner?: IUnitOwner | null;
  property_management_contract?: IPropertyManagementContract | null;
}

export const downloadDocument = async (
  documentId: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/documents/${documentId}/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const downloadPropertyDocument = async (
  propertyId: number,
  documentId: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/properties/${propertyId}/documents/${documentId}/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const deleteDocument = async (document_id: number): Promise<boolean> => {
  const response = await fetchWithAuth(`${API_URL}/documents/${document_id}`, {
    method: "DELETE",
  });

  return response.status === 200;
};
